import * as XLSX from "xlsx-js-style";

export type RowStatus = "same" | "new" | "revised" | "canceled";

export interface RowResult {
	status: RowStatus;
	changed: number[];
	oldIndex: number;
}

export interface SheetSummary {
	sheet: string;
	added: number;
	revised: number;
	canceled: number;
}

export const FILL = {
	new: "00B0F0", // blue
	revised: "FFFF00", // yellow
	canceled: "FF0000", // red
};

const norm = (v: any): string =>
	String(v === null || v === undefined ? "" : v)
		.replace(/\u00a0/g, " ")
		.replace(/\s+/g, " ")
		.trim();

const rowSignature = (row: any[]) => row.map(norm).join("\u0001");

const isBlankRow = (row: any[]) => row.every(c => !norm(c));

export const toGrid = (ws: XLSX.WorkSheet): any[][] =>
	XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: "" }) as any[][];

/** Longest common subsequence of identical rows — used as alignment anchors. */
function lcsPairs(a: string[], b: string[]): Array<[number, number]> {
	const n = a.length;
	const m = b.length;
	const dp: Uint32Array[] = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));

	for (let i = n - 1; i >= 0; i--) {
		for (let j = m - 1; j >= 0; j--) {
			dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
		}
	}

	const pairs: Array<[number, number]> = [];
	let i = 0;
	let j = 0;
	while (i < n && j < m) {
		if (a[i] === b[j]) {
			pairs.push([i, j]);
			i++;
			j++;
		} else if (dp[i + 1][j] >= dp[i][j + 1]) {
			i++;
		} else {
			j++;
		}
	}
	return pairs;
}

function cellSimilarity(a: string, b: string): number {
	if (a === b) return 1;
	if (a.length < 20 && b.length < 20) return 0;

	const ta = new Set(a.toLowerCase().split(/\W+/).filter(Boolean));
	const tb = new Set(b.toLowerCase().split(/\W+/).filter(Boolean));
	if (!ta.size || !tb.size) return 0;

	let intersection = 0;
	ta.forEach(t => {
		if (tb.has(t)) intersection++;
	});
	return (2 * intersection) / (ta.size + tb.size);
}

function rowSimilarity(r1: any[], r2: any[]): number {
	const len = Math.max(r1.length, r2.length);
	let total = 0;
	let count = 0;

	for (let k = 0; k < len; k++) {
		const a = norm(r1[k]);
		const b = norm(r2[k]);
		if (!a || !b) continue;
		count++;
		total += cellSimilarity(a, b);
	}
	return count ? total / count : 0;
}

function changedColumns(r1: any[], r2: any[]): number[] {
	const out: number[] = [];
	const len = Math.max(r1.length, r2.length);
	for (let k = 0; k < len; k++) {
		if (norm(r1[k]) !== norm(r2[k])) out.push(k);
	}
	return out;
}

/**
 * Aligns the two grids and classifies every row of the new grid.
 * Returns the per-row verdict plus the old-grid rows that disappeared.
 */
export function diffGrid(
	oldRows: any[][],
	newRows: any[][],
	threshold = 0.5
): { result: RowResult[]; canceled: number[] } {
	const anchors = lcsPairs(oldRows.map(rowSignature), newRows.map(rowSignature));
	const result: RowResult[] = newRows.map(() => ({ status: "same", changed: [], oldIndex: -1 }));
	const canceled: number[] = [];

	// Split both grids into the gaps between identical (anchor) rows.
	const segments: Array<[number, number, number, number]> = [];
	let prevOld = -1;
	let prevNew = -1;
	anchors.forEach(([oi, ni]) => {
		segments.push([prevOld + 1, oi, prevNew + 1, ni]);
		prevOld = oi;
		prevNew = ni;
		result[ni].oldIndex = oi;
	});
	segments.push([prevOld + 1, oldRows.length, prevNew + 1, newRows.length]);

	segments.forEach(([o0, o1, n0, n1]) => {
		const olds: number[] = [];
		const news: number[] = [];
		for (let o = o0; o < o1; o++) if (!isBlankRow(oldRows[o])) olds.push(o);
		for (let n = n0; n < n1; n++) if (!isBlankRow(newRows[n])) news.push(n);

		// 1. Pair the most similar rows first.
		const candidates: Array<{ n: number; o: number; score: number }> = [];
		news.forEach(n =>
			olds.forEach(o => {
				const score = rowSimilarity(oldRows[o], newRows[n]);
				if (score >= threshold) candidates.push({ n, o, score });
			})
		);
		candidates.sort((a, b) => b.score - a.score);

		const usedOld = new Set<number>();
		const usedNew = new Set<number>();
		candidates.forEach(({ n, o }) => {
			if (usedOld.has(o) || usedNew.has(n)) return;
			usedOld.add(o);
			usedNew.add(n);
			result[n] = { status: "revised", changed: changedColumns(oldRows[o], newRows[n]), oldIndex: o };
		});

		// 2. Leftovers that line up 1:1 are the same slot with rewritten content.
		const leftNew = news.filter(n => !usedNew.has(n));
		const leftOld = olds.filter(o => !usedOld.has(o));
		if (leftNew.length && leftNew.length === leftOld.length) {
			leftNew.forEach((n, idx) => {
				const o = leftOld[idx];
				usedOld.add(o);
				usedNew.add(n);
				result[n] = { status: "revised", changed: changedColumns(oldRows[o], newRows[n]), oldIndex: o };
			});
		}

		// 3. Whatever is left is genuinely added or removed.
		news.forEach(n => {
			if (!usedNew.has(n)) result[n] = { status: "new", changed: [], oldIndex: -1 };
		});
		olds.forEach(o => {
			if (!usedOld.has(o)) canceled.push(o);
		});
	});

	return { result, canceled };
}

function paint(ws: XLSX.WorkSheet, r: number, c: number, rgb: string) {
	const addr = XLSX.utils.encode_cell({ r, c });
	if (!ws[addr]) ws[addr] = { t: "s", v: "" };
	const cell = ws[addr];

	// Blank cells (t: "z") are skipped by the writer, so their fill would be
	// dropped — turn them into empty text cells so a cleared value shows red.
	if (cell.t === "z" || cell.v === undefined) {
		cell.t = "s";
		cell.v = "";
	}

	cell.s = {
		...(cell.s || {}),
		fill: { patternType: "solid", fgColor: { rgb } },
	};
}

export function highlightWorkbook(
	wbOld: XLSX.WorkBook,
	wbNew: XLSX.WorkBook
): { workbook: XLSX.WorkBook; summary: SheetSummary[] } {
	const summary: SheetSummary[] = [];

	wbNew.SheetNames.forEach((name, sheetIdx) => {
		const ws = wbNew.Sheets[name];
		if (!ws || !ws["!ref"]) return;

		const oldName = wbOld.SheetNames.includes(name) ? name : wbOld.SheetNames[sheetIdx];
		const wsOld = oldName ? wbOld.Sheets[oldName] : undefined;

		const newRows = toGrid(ws);
		const oldRows = wsOld && wsOld["!ref"] ? toGrid(wsOld) : [];

		const { result, canceled } = diffGrid(oldRows, newRows);

		const range = XLSX.utils.decode_range(ws["!ref"]);
		const firstCol = range.s.c;
		const lastCol = range.e.c;

		let blue = 0;
		let yellow = 0;
		let red = 0;

		result.forEach((row, r) => {
			if (row.status === "same") return;

			// A brand new row: every populated cell is new.
			if (row.status === "new") {
				for (let c = firstCol; c <= lastCol; c++) {
					if (norm(newRows[r][c])) {
						paint(ws, r, c, FILL.new);
						blue++;
					}
				}
				return;
			}

			// A matched row: colour each changed cell by what happened to it.
			const before = oldRows[row.oldIndex] || [];
			row.changed.forEach(c => {
				if (c < firstCol || c > lastCol) return;
				const was = norm(before[c]);
				const now = norm(newRows[r][c]);

				if (!was && now) {
					paint(ws, r, c, FILL.new);
					blue++;
				} else if (was && !now) {
					paint(ws, r, c, FILL.canceled);
					red++;
				} else {
					paint(ws, r, c, FILL.revised);
					yellow++;
				}
			});
		});

		// A row that vanished entirely has no cell in the new sheet to colour,
		// so it is reported in the summary only.
		summary.push({
			sheet: name,
			added: blue,
			revised: yellow,
			canceled: red + canceled.length,
		});
	});

	return { workbook: wbNew, summary };
}

export async function readWorkbook(file: File): Promise<XLSX.WorkBook> {
	const buffer = await file.arrayBuffer();
	return XLSX.read(new Uint8Array(buffer), { type: "array", cellStyles: true });
}
